import React, { useEffect, useRef, useState } from 'react';
import { Bell, ChevronDown, HelpCircle, LogOut, Menu, Search, Package, Users, Store } from 'lucide-react';
import { erpSearch } from '../../../services/erpApi';
import { initials } from '../ui';

const TYPE_ICON = { SKU: Package, Customer: Users, Vendor: Store };

const useDebounced = (v, ms) => {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
};

const GlobalSearch = ({ go }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const dq = useDebounced(q, 250);
  const box = useRef(null);

  useEffect(() => {
    if (dq.trim().length < 2) { setItems([]); return; }
    erpSearch(dq).then((r) => { setItems(r.data.items || []); setOpen(true); }).catch(() => {});
  }, [dq]);
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={box} className="relative flex-1 max-w-2xl">
      <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--erp-muted)' }} />
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => items.length && setOpen(true)}
        placeholder="Search (Customer, Product, Order, Invoice, etc...)" data-testid="erp-global-search"
        className="erp-input erp-input-icon erp-input-lg" style={{ background: '#f5f7fb', borderColor: 'transparent' }} />
      {open && (
        <div className="absolute z-50 mt-2 w-full erp-card overflow-hidden erp-rise" data-testid="erp-search-results">
          {items.length === 0 ? <p className="px-4 py-3 text-sm" style={{ color: 'var(--erp-muted)' }}>No matches for “{dq}”</p> : items.map((it) => {
            const Icon = TYPE_ICON[it.type] || Package;
            return (
              <button key={`${it.type}-${it.id}`} type="button" onClick={() => { go(it.view); setOpen(false); setQ(''); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#f5f7fb] transition-colors">
                <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: 'var(--erp-blue-soft)', color: 'var(--erp-blue)' }}><Icon size={15} /></span>
                <span className="flex-1 min-w-0"><span className="block text-sm font-semibold truncate">{it.title}</span><span className="block text-xs truncate" style={{ color: 'var(--erp-muted)' }}>{it.subtitle}</span></span>
                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--erp-muted)' }}>{it.type}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Topbar = ({ user, go, onToggleSidebar, alertCount = 0, onLogout }) => {
  const [menu, setMenu] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-white border-b" style={{ borderColor: 'var(--erp-line)' }} data-testid="erp-topbar">
      <div className="h-16 px-4 sm:px-6 flex items-center gap-4">
        <button type="button" onClick={onToggleSidebar} className="h-10 w-10 rounded-lg grid place-items-center hover:bg-gray-100 transition-colors" data-testid="erp-sidebar-toggle"><Menu size={20} /></button>
        <GlobalSearch go={go} />
        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          <button type="button" onClick={() => go('dashboard')} className="relative h-10 w-10 rounded-lg grid place-items-center hover:bg-gray-100 transition-colors" data-testid="erp-notifications">
            <Bell size={19} />
            {alertCount > 0 && <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10.5px] font-bold grid place-items-center" data-testid="erp-alert-count">{alertCount}</span>}
          </button>
          <button type="button" className="h-10 w-10 rounded-lg grid place-items-center hover:bg-gray-100 transition-colors" data-testid="erp-help"><HelpCircle size={19} /></button>
          <div className="relative">
            <button type="button" onClick={() => setMenu((m) => !m)} className="flex items-center gap-3 pl-2 pr-1 h-11 rounded-xl hover:bg-gray-100 transition-colors" data-testid="erp-user-menu">
              <span className="h-9 w-9 rounded-full grid place-items-center text-white text-sm font-bold" style={{ background: 'var(--erp-navy)' }}>{initials(user.name)}</span>
              <span className="hidden sm:block text-left leading-tight"><span className="block text-sm font-bold">{user.name}</span><span className="block text-xs capitalize" style={{ color: 'var(--erp-muted)' }}>{user.role}</span></span>
              <ChevronDown size={16} style={{ color: 'var(--erp-muted)' }} />
            </button>
            {menu && (
              <div className="absolute right-0 mt-2 w-56 erp-card p-2 erp-rise" onMouseLeave={() => setMenu(false)}>
                <div className="px-3 py-2 text-xs" style={{ color: 'var(--erp-muted)' }}>{user.email}</div>
                <button type="button" onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors" data-testid="erp-logout"><LogOut size={15} /> Sign out</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="erp-tagline-band h-8 hidden md:flex items-center justify-end pr-8">
        <span className="text-[12.5px] font-bold tracking-wide" style={{ color: 'var(--erp-navy)' }}>PRECISION TODAY. A STRONGER TOMORROW.</span>
      </div>
    </header>
  );
};
